import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';

import { matchFactoryLocale, type FactoryLocaleCode } from '@omt-global/factory-site-schema/runtime';
import type { PublicFactorySiteProjection } from '@omt-global/factory-site-schema';

import './styles.css';

const rootElement = document.querySelector('#root');
if (!rootElement) throw new Error('Public runtime root element was not found.');
const root = rootElement;
const unavailable = 'This website is not available yet.';

function publishedPdpSsrRoot(element: Element): boolean {
  return element.getAttribute('data-factory-ssr') === 'pdp' && element.innerHTML.trim().length > 0;
}

function revisionFromSiteDataResponse(response: Response): string | undefined {
  const header = response.headers.get('x-factory-site-revision')?.trim();
  if (header) return header;
  const etag = response.headers.get('etag')?.trim();
  if (!etag) return undefined;
  const opaque = etag.replace(/^W\//u, '').replaceAll('"', '').trim();
  return opaque || undefined;
}

function readPublishedPdpSsrMarker(): { locale: FactoryLocaleCode; revision: string; route: string; v: number } | undefined {
  const script = document.getElementById('factory-ssr-revision');
  if (!script || script.getAttribute('type') !== 'application/json') return undefined;
  try {
    const parsed = JSON.parse(script.textContent ?? '') as Record<string, unknown>;
    if (parsed.v !== 1) return undefined;
    if (typeof parsed.revision !== 'string' || !/^[0-9a-f]{64}$/u.test(parsed.revision)) return undefined;
    const locale = typeof parsed.locale === 'string' ? matchFactoryLocale(parsed.locale) : undefined;
    if (!locale) return undefined;
    if (typeof parsed.route !== 'string' || !parsed.route.startsWith('/')) return undefined;
    return {
      locale,
      revision: parsed.revision,
      route: parsed.route,
      v: 1,
    };
  } catch {
    return undefined;
  }
}

function looksLikePublicProjection(value: unknown): value is PublicFactorySiteProjection {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return record.schemaVersion === 1
    && record.createdBy === 'OMT Global Limited'
    && Array.isArray(record.products)
    && typeof record.factory === 'object'
    && record.factory !== null
    && typeof record.site === 'object'
    && record.site !== null;
}

function markerMatchesLocation(
  marker: { locale: string; route: string },
  pathname: string,
): boolean {
  const firstSegment = pathname.split('/').filter(Boolean).at(0) ?? '';
  return marker.route === pathname && matchFactoryLocale(firstSegment) === matchFactoryLocale(marker.locale);
}

let inMemorySiteData: { json: unknown; revision?: string } | undefined;

async function fetchSiteData(): Promise<{ json: unknown; response: Response; revision?: string | undefined }> {
  if (inMemorySiteData) {
    return {
      json: inMemorySiteData.json,
      response: new Response(null, { status: 200 }),
      ...(inMemorySiteData.revision ? { revision: inMemorySiteData.revision } : {}),
    };
  }
  const response = await fetch('/site-data.json', { cache: 'no-store', credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Public site data request failed with status ${String(response.status)}.`);
  const revision = revisionFromSiteDataResponse(response);
  const json: unknown = await response.json();
  inMemorySiteData = revision ? { json, revision } : { json };
  return {
    json,
    response,
    ...(revision ? { revision } : {}),
  };
}

async function bootValidated(existingJson?: unknown): Promise<void> {
  const [payload, hintsResponse] = await Promise.all([
    existingJson === undefined ? fetchSiteData() : Promise.resolve({ json: existingJson, revision: undefined, response: undefined }),
    fetch('/runtime-hints.json', { cache: 'no-store', credentials: 'same-origin' }).then((hints) => hints.ok ? hints : undefined).catch(() => undefined),
  ]);
  const [
    { matchFactoryLocale: matchLocale, publicFactorySiteProjectionSchema },
    { FactorySiteRuntime, resolveVisitorLocale },
    { loadPublicMessages },
    { resolvePublicRoute, siteLocaleToPublicLocale },
  ] = await Promise.all([
    import('@omt-global/factory-site-schema'),
    import('./runtime.js'),
    import('./i18n/load-public-messages.js'),
    import('@omt-global/factory-site-renderer'),
  ]);
  const data = publicFactorySiteProjectionSchema.parse(payload.json);
  const automotiveRuntime = data.site.businessClass === 'automotive_parts' ? await import('./automotive-catalog.js') : undefined;
  const hints = hintsResponse
    ? await hintsResponse.json() as { countryHint?: string | null }
    : {};
  const pathname = globalThis.location?.pathname ?? '/';
  const firstSegment = pathname.split('/').filter(Boolean).at(0) ?? null;
  const urlLocaleFromPath = firstSegment ? matchLocale(firstSegment) : undefined;
  const visitorLocale = resolveVisitorLocale({
    acceptLanguages: globalThis.navigator?.languages ?? (globalThis.navigator?.language ? [globalThis.navigator.language] : []),
    ...(hints.countryHint ? { countryHint: hints.countryHint } : {}),
    defaultLocale: data.site.defaultLocale,
    enabledLocales: data.site.enabledLocales,
    manualLocale: urlLocaleFromPath ? null : globalThis.localStorage?.getItem('factory-site:visitor-locale'),
    urlLocale: firstSegment,
  });
  const requestedRoute = resolvePublicRoute(pathname, siteLocaleToPublicLocale(data.site.defaultLocale));
  const browserLanding = pathname === '/' || pathname === '/index.html';
  await loadPublicMessages(browserLanding ? visitorLocale : requestedRoute.locale);
  const tree = <StrictMode><FactorySiteRuntime automotiveRuntime={automotiveRuntime} data={data} {...(hints.countryHint ? { countryHint: hints.countryHint } : {})} /></StrictMode>;
  createRoot(root).render(tree);
}

async function bootRevisionMatched(): Promise<boolean> {
  if (!publishedPdpSsrRoot(root)) return false;
  const marker = readPublishedPdpSsrMarker();
  const pathname = globalThis.location?.pathname ?? '/';
  if (!marker || !markerMatchesLocation(marker, pathname)) return false;
  const payload = await fetchSiteData();
  if (!payload.revision || payload.revision !== marker.revision) {
    await bootValidated(payload.json);
    return true;
  }
  if (!looksLikePublicProjection(payload.json)) {
    await bootValidated(payload.json);
    return true;
  }
  const [
    { FactorySiteRuntime },
    { loadPublicMessages },
  ] = await Promise.all([
    import('./runtime.js'),
    import('./i18n/load-public-messages.js'),
  ]);
  const publicOrigin = document.head.querySelector('meta[name="public-origin"]')?.getAttribute('content') ?? '';
  await loadPublicMessages(marker.locale);
  hydrateRoot(root, (
    <StrictMode>
      <FactorySiteRuntime data={payload.json} path={marker.route} {...(publicOrigin ? { publicOrigin } : {})} />
    </StrictMode>
  ));
  return true;
}

async function start(): Promise<void> {
  const embedded = document.getElementById('omtauto-bootstrap');
  if (embedded?.getAttribute('type') === 'application/json' && root.getAttribute('data-factory-ssr') === 'automotive') {
    const [{ publicFactorySiteProjectionSchema }, { FactorySiteRuntime }, { loadPublicMessages }, { resolvePublicRoute, siteLocaleToPublicLocale }] = await Promise.all([
      import('@omt-global/factory-site-schema'), import('./runtime.js'), import('./i18n/load-public-messages.js'), import('@omt-global/factory-site-renderer'),
    ]);
    const data = publicFactorySiteProjectionSchema.parse(JSON.parse(embedded.textContent ?? ''));
    const automotiveRuntime = await import('./automotive-catalog.js');
    const path = `${globalThis.location.pathname}${globalThis.location.search}`;
    if (data.automotiveCatalog?.path === path) {
      await loadPublicMessages(resolvePublicRoute(path.split('?')[0] ?? '/', siteLocaleToPublicLocale(data.site.defaultLocale)).locale);
      const publicOrigin = document.head.querySelector('meta[name="public-origin"]')?.getAttribute('content') ?? '';
      hydrateRoot(root, <StrictMode><FactorySiteRuntime automotiveRuntime={automotiveRuntime} data={data} path={path} publicOrigin={publicOrigin} /></StrictMode>);
      return;
    }
  }
  if (await bootRevisionMatched()) return;
  await bootValidated();
}

void start().catch((error: unknown) => {
  root.textContent = unavailable;
  console.error(error);
});
